import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from 'context/AuthContext'
import { useEvent } from 'context/EventContext'
import { scannerApi, extractData } from 'shared/api/scanner'
import { PERMISSIONS, hasPermission } from 'shared/util/permissions'
import { Card } from 'shared/components/ui/primitives'
import { IconScan } from 'shared/components/icons'

function ScannerHome() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { event, scanHistory } = useEvent()
  const [counts, setCounts] = useState(null)
  const canScan = hasPermission(user, eventId, PERMISSIONS.SCANNER)

  useEffect(() => {
    let mounted = true
    if (!hasPermission(user, eventId, PERMISSIONS.GET_REPORT)) return undefined
    scannerApi
      .reports(eventId)
      .then((res) => {
        if (mounted) setCounts(extractData(res))
      })
      .catch(() => {})
    return () => {
      mounted = false
    }
  }, [eventId, user])

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-5">
      <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted">Now scanning</p>
      <h2 className="mb-5 text-4xl font-bold">{event?.sTitle || 'Event'}</h2>

      <button
        type="button"
        disabled={!canScan}
        onClick={() => navigate(`/events/${eventId}/scan`)}
        className="relative mb-5 flex min-h-[220px] w-full flex-col items-center justify-center rounded-card bg-theme text-light shadow-glow disabled:bg-dark-600"
      >
        <span className="mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-light/15">
          <IconScan className="h-10 w-10" />
        </span>
        <span className="text-4xl font-bold">Scan ticket</span>
        <span className="mt-1 text-sm text-light/80">{canScan ? 'Open camera or enter a code' : 'Scan permission required'}</span>
      </button>

      {counts && (
        <div className="mb-5 grid grid-cols-2 gap-2 rounded-2xl bg-surface p-3 text-center">
          <div>
            <p className="mb-0 text-5xl font-bold text-success">{counts.nPeopleInsideEvent ?? counts.nInsideEvent ?? 0}</p>
            <p className="mb-0 text-xs text-muted">Inside</p>
          </div>
          <div>
            <p className="mb-0 text-5xl font-bold text-primary">{counts.nPeopleOutsideEvent ?? counts.nOutsideEvent ?? 0}</p>
            <p className="mb-0 text-xs text-muted">Outside</p>
          </div>
        </div>
      )}

      <h3 className="mb-3 text-xl font-semibold">Recent scans</h3>
      {scanHistory.length === 0
        ? (
        <Card>
          <p className="mb-0 text-sm text-muted">Recent admits will show up here so you can double-check the last few scans.</p>
        </Card>
          )
        : (
        <div className="space-y-2">
          {scanHistory.map((item, index) => (
            <div key={`${item.at}-${index}`} className="flex items-center justify-between rounded-2xl bg-surface px-4 py-3">
              <div>
                <p className="mb-0 font-semibold">{item.name || item.code}</p>
                <p className="mb-0 text-xs text-muted">{item.code}</p>
              </div>
              <span
                className={`rounded-full px-2 py-1 text-xxs font-bold uppercase ${
                  item.status === 'admitted'
                    ? 'bg-[#E2F4EE] text-[#008F61]'
                    : item.status === 'used'
                      ? 'bg-primary-900 text-primary-200'
                      : 'bg-theme-900 text-theme'
                }`}
              >
                {item.status}
              </span>
            </div>
          ))}
        </div>
          )}
    </div>
  )
}

export default ScannerHome
